import {ChangeDetectorRef, Component, effect, inject, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {NgbModal, NgbNavModule} from '@ng-bootstrap/ng-bootstrap';
import {RoleAssignModal} from './role-assign-modal';
import {RoleAssignApiService} from './role-assign-api.service';
import {RoleAssignmentAction, RoleAssignmentDto, RoleAssignmentRequest} from '../../../models/generated-models';
import {ConfirmDialogService} from '../../shared/commons/dialog/confirm-dialog/confirm-dialog.service';
import {ToastService} from '../../shared/commons/toast/toast-service';
import {PageTitleComponent} from '../../shared/commons/page-title/page-title.component';
import {CurrentUserContextService} from '../../shared/services/current-user-context.service';
import {roleAssignmentActionLabel} from '../../app.constants';

interface RoleAssignmentRow {
  id: number;
  assignee: string;
  assignedItems: string;
  timestamp: string;
  status: string;
  canEdit: boolean;
  dto: RoleAssignmentDto;
}

@Component({
  selector: 'app-role-assign',
  standalone: true,
  imports: [CommonModule, PageTitleComponent, NgbNavModule],
  templateUrl: './role-assign.html',
  styleUrl: './role-assign.scss',
})
export class RoleAssign implements OnInit {
  private readonly api = inject(RoleAssignApiService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly toast = inject(ToastService);
  private readonly modalService = inject(NgbModal);
  protected readonly userContext = inject(CurrentUserContextService);

  activeTab: 'assign' | 'received' = 'assign';
  roleAssignments: RoleAssignmentRow[] = [];

  private openedOnReceived = false;

  constructor() {
    // An agent most likely came for the grants they received, so that tab opens first — once;
    // they may still switch to มอบสิทธิ์ใช้งาน for their own businesses (work item 173).
    effect(() => {
      if (!this.openedOnReceived && this.userContext.hasAnyAuthority('AGENT_OPERATOR') && this.activeTab === 'assign') {
        this.openedOnReceived = true;
        this.activeTab = 'received';
        this.loadRoleAssignments();
      }
    });
  }

  ngOnInit(): void {
    this.loadRoleAssignments();
  }

  loadRoleAssignments(): void {
    const source = this.activeTab === 'received'
      ? this.api.listReceived()
      : this.api.listGranted();
    source.subscribe({
      next: (rows) => {
        this.roleAssignments = (rows ?? []).map((row) => this.toRow(row));
        this.cdr.detectChanges();
      },
      error: () => {
        this.roleAssignments = [];
        this.cdr.detectChanges();
      }
    });
  }

  onTabChange(tab: 'assign' | 'received'): void {
    this.activeTab = tab;
    this.loadRoleAssignments();
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'ACTIVE':
        return 'ตอบรับแล้ว';
      case 'EXPIRED':
        return 'หมดอายุ';
      case 'REVOKED':
        return 'เพิกถอน';
      case 'SCHEDULED':
        return 'รอเริ่มใช้งาน';
      case 'PENDING':
        return 'รอการตอบรับ';
      case 'REJECTED':
        return 'ปฏิเสธแล้ว';
      default:
        return status;
    }
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'ACTIVE':
        return 'status-approved';
      case 'EXPIRED':
      case 'REVOKED':
      case 'REJECTED':
        return 'status-rejected';
      case 'SCHEDULED':
      case 'PENDING':
        return 'status-pending';
      default:
        return 'status-pending';
    }
  }

  openAddModal(): void {
    const modalRef = this.modalService.open(RoleAssignModal, {
      size: 'lg',
      centered: true,
      backdrop: 'static'
    });

    modalRef.componentInstance.saved.subscribe((request: RoleAssignmentRequest) => {
      this.api.create(request).subscribe({
        next: () => {
          this.toast.success('มอบสิทธิ์เรียบร้อยแล้ว');
          this.loadRoleAssignments();
        }
        // errors surfaced by the global HTTP interceptors
      });
    });
  }

  openEditModal(row: RoleAssignmentRow): void {
    const modalRef = this.modalService.open(RoleAssignModal, {
      size: 'lg',
      centered: true,
      backdrop: 'static'
    });
    modalRef.componentInstance.editValue = row.dto;

    modalRef.componentInstance.saved.subscribe((request: RoleAssignmentRequest) => {
      this.api.update(row.id, request).subscribe({
        next: () => {
          this.toast.success('แก้ไขการมอบสิทธิ์เรียบร้อยแล้ว');
          this.loadRoleAssignments();
        }
      });
    });
  }

  deleteAssignment(row: RoleAssignmentRow): void {
    this.confirmDialog.open({
      title: 'ลบการมอบสิทธิ์',
      content: `ต้องการลบการมอบสิทธิ์ของ ${row.assignee} ใช่หรือไม่`,
      confirmButtonMsg: 'ลบ',
      closeButtonMsg: 'ยกเลิก'
    }).subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }
      this.api.delete(row.id).subscribe({
        next: () => {
          this.toast.success('ลบการมอบสิทธิ์เรียบร้อยแล้ว');
          this.loadRoleAssignments();
        }
      });
    });
  }

  private toRow(dto: RoleAssignmentDto): RoleAssignmentRow {
    const assignee = this.activeTab === 'received'
      ? (dto.grantorName || '-')
      : (dto.delegateName || dto.delegateTaxId || '-');
    return {
      id: dto.id ?? 0,
      assignee,
      assignedItems: this.formatAssignedItems(dto),
      timestamp: this.formatTime(dto.createdAt),
      status: dto.status ?? '',
      canEdit: !!dto.canEdit,
      dto
    };
  }

  private formatAssignedItems(dto: RoleAssignmentDto): string {
    const actionStr = this.formatActions(dto.actions);
    const certStr = dto.standardName || dto.standardCode || dto.certificateNo;
    if (certStr) {
      return `${actionStr}`;
    }
    return actionStr;
  }

  private formatActions(actions?: RoleAssignmentAction[]): string {
    if (!actions || actions.length === 0) {
      return 'บันทึกซื้อ-ขาย';
    }
    return actions.map((a) => roleAssignmentActionLabel(a)).join(', ');
  }

  private formatTime(value: string | Date | undefined): string {
    if (!value) {
      return '-';
    }
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString('th-TH');
  }
}
